import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  Shield, 
  Briefcase, 
  HardHat, 
  UserCircle,
  Info,
  Clock,
  UserPlus,
  Mail,
  Check,
  X,
  RefreshCw,
  Copy,
  CheckCircle,
  AlertCircle,
  Trash2
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import GlassPanel from '../../components/GlassPanel';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Role definitions with icons and descriptions
const roleDefinitions = [
  {
    role: 'Owner / Founder',
    icon: Shield,
    color: 'var(--accent-primary)',
    permissions: [
      'Full system access',
      'Finance-Life, AI, billing, integrations',
      'Staff wellbeing & safeguarding insights'
    ]
  },
  {
    role: 'Manager',
    icon: Briefcase,
    color: 'var(--status-info)',
    invitable: true,
    permissions: [
      'Jobs, schedules, materials, notifications',
      'Finance access: Optional (toggle below)',
      'Staff invite: Optional (toggle below)',
      'Margins & Price Intel: Founder-only'
    ]
  },
  {
    role: 'Staff',
    icon: HardHat,
    color: 'var(--status-success)',
    invitable: true,
    permissions: [
      'Jobs (view & update assigned tasks)',
      'Scheduling / Gantt (view + mark complete)',
      'Materials (view only)',
      'No finance, no invites, no delete'
    ]
  },
  {
    role: 'Clients',
    icon: UserCircle,
    color: 'var(--text-muted)',
    subLabel: '(later phase)',
    permissions: [
      'Read-only portal',
      'Quotes, invoices, project progress',
      'No internal systems or AI logic'
    ]
  }
];

// Invite Modal Component
function InviteModal({ isOpen, onClose, onSuccess, orgId, canInviteManager, canInviteStaff }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState(canInviteManager ? 'MANAGER' : 'STAFF');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(null);

    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API}/api/invites/?org_id=${orgId}`,
        { email, role },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess(response.data);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create invite');
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    if (success?.invite_link) {
      navigator.clipboard.writeText(success.invite_link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    setEmail('');
    setError('');
    setSuccess(null);
    setCopied(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={modalStyles.overlay} onClick={handleClose} data-testid="invite-modal">
      <div style={modalStyles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={modalStyles.header}>
          <h3 style={modalStyles.title}>
            <UserPlus size={20} />
            Invite Team Member
          </h3>
          <button onClick={handleClose} style={modalStyles.closeBtn}>
            <X size={20} />
          </button>
        </div>

        {success ? (
          <div style={modalStyles.successContent}>
            <CheckCircle size={48} style={{ color: '#22c55e', margin: '0 auto 16px', display: 'block' }} />
            <h4 style={modalStyles.successTitle}>Invite Created!</h4>
            <p style={modalStyles.successText}>
              Share this link with <strong>{success.email}</strong>
              <br />
              <span style={{ 
                display: 'inline-block',
                marginTop: '8px',
                padding: '4px 10px',
                background: success.role === 'MANAGER' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(34, 197, 94, 0.1)',
                color: success.role === 'MANAGER' ? '#3b82f6' : '#22c55e',
                borderRadius: '20px',
                fontSize: '0.75rem',
                fontWeight: 500
              }}>
                {success.role}
              </span>
            </p>
            
            <div style={modalStyles.linkBox}>
              <input
                type="text"
                value={success.invite_link}
                readOnly
                style={modalStyles.linkInput}
                data-testid="invite-link-input"
              />
              <button
                onClick={copyLink}
                style={modalStyles.copyBtn}
                data-testid="copy-link-btn"
              >
                {copied ? <Check size={16} /> : <Copy size={16} />}
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>

            <p style={modalStyles.expiryNote}>
              Link expires in 7 days
            </p>

            <button onClick={handleClose} style={modalStyles.doneBtn}>
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={modalStyles.form}>
            <div style={modalStyles.fieldGroup}>
              <label style={modalStyles.label}>Email Address</label>
              <div style={modalStyles.inputWrapper}>
                <Mail size={18} style={modalStyles.inputIcon} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="colleague@company.com"
                  style={modalStyles.input}
                  data-testid="invite-email-input"
                  required
                />
              </div>
            </div>

            <div style={modalStyles.fieldGroup}>
              <label style={modalStyles.label}>Role</label>
              <div style={modalStyles.roleSelector}>
                {/* Manager Option - only if can invite managers */}
                {canInviteManager && (
                  <div
                    style={{
                      ...modalStyles.roleOption,
                      ...(role === 'MANAGER' ? modalStyles.roleOptionActive : {})
                    }}
                    onClick={() => setRole('MANAGER')}
                    data-testid="role-manager-option"
                  >
                    <Briefcase size={20} />
                    <div>
                      <div style={modalStyles.roleTitle}>Manager</div>
                      <div style={modalStyles.roleDesc}>Jobs, tasks, materials. Finance optional.</div>
                    </div>
                  </div>
                )}
                
                {/* Staff Option - only if can invite staff */}
                {canInviteStaff && (
                  <div
                    style={{
                      ...modalStyles.roleOption,
                      ...(role === 'STAFF' ? { ...modalStyles.roleOptionActive, borderColor: '#22c55e', background: 'rgba(34, 197, 94, 0.1)' } : {})
                    }}
                    onClick={() => setRole('STAFF')}
                    data-testid="role-staff-option"
                  >
                    <HardHat size={20} style={role === 'STAFF' ? { color: '#22c55e' } : {}} />
                    <div>
                      <div style={{ ...modalStyles.roleTitle, color: role === 'STAFF' ? '#22c55e' : undefined }}>Staff</div>
                      <div style={modalStyles.roleDesc}>Assigned tasks, view materials. No finance.</div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Role description */}
              <div style={{
                marginTop: '12px',
                padding: '10px',
                background: role === 'MANAGER' ? 'rgba(59, 130, 246, 0.05)' : 'rgba(34, 197, 94, 0.05)',
                borderRadius: '8px',
                border: `1px dashed ${role === 'MANAGER' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(34, 197, 94, 0.3)'}`,
                fontSize: '0.75rem',
                color: '#94a3b8'
              }}>
                {role === 'MANAGER' ? (
                  <>
                    <strong style={{ color: '#3b82f6' }}>Manager Access:</strong> Full operational access to jobs, scheduling, materials.
                    Finance access can be granted separately after they join.
                  </>
                ) : (
                  <>
                    <strong style={{ color: '#22c55e' }}>Staff Access:</strong> View and update assigned tasks, view scheduling and materials.
                    No finance, no invites, no delete permissions.
                  </>
                )}
              </div>
            </div>

            {error && (
              <div style={modalStyles.errorBox}>
                <AlertCircle size={16} />
                {error}
              </div>
            )}

            <div style={modalStyles.actions}>
              <button
                type="button"
                onClick={handleClose}
                style={modalStyles.cancelBtn}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !email}
                style={{
                  ...modalStyles.submitBtn,
                  background: role === 'MANAGER' ? '#3b82f6' : '#22c55e',
                  opacity: loading || !email ? 0.6 : 1
                }}
                data-testid="send-invite-btn"
              >
                {loading ? 'Sending...' : `Invite ${role === 'MANAGER' ? 'Manager' : 'Staff'}`}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// Main Component
export function TeamMembers() {
  const navigate = useNavigate();
  const { isFounder, loading, orgId, user } = useAuth();
  const [invites, setInvites] = useState([]);
  const [invitesLoading, setInvitesLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(true);
  const [invitePermissions, setInvitePermissions] = useState({
    can_invite: false,
    can_invite_manager: false,
    can_invite_staff: false,
    role: null
  });

  // Fetch invite permissions
  const fetchInvitePermissions = useCallback(async () => {
    if (!orgId) return;
    
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${API}/api/invites/my-permissions?org_id=${orgId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInvitePermissions(response.data);
    } catch (err) {
      console.error('Failed to fetch invite permissions:', err);
    }
  }, [orgId]);

  // Fetch team members
  const fetchTeamMembers = useCallback(async () => {
    if (!orgId) return;
    
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${API}/api/invites/team-members?org_id=${orgId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTeamMembers(response.data.members);
    } catch (err) {
      console.error('Failed to fetch team members:', err);
    } finally {
      setMembersLoading(false);
    }
  }, [orgId]);

  // Fetch invites
  const fetchInvites = useCallback(async () => {
    if (!orgId) return;
    
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${API}/api/invites/?org_id=${orgId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInvites(response.data.invites);
    } catch (err) {
      console.error('Failed to fetch invites:', err);
    } finally {
      setInvitesLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    if (!loading && (isFounder() || user?.role === 'MANAGER')) {
      fetchInvitePermissions();
      fetchInvites();
      fetchTeamMembers();
    }
  }, [loading, isFounder, user, fetchInvitePermissions, fetchInvites, fetchTeamMembers]);

  // Redirect users without any access
  useEffect(() => {
    if (!loading && !isFounder() && user?.role !== 'MANAGER') {
      navigate('/command-centre');
    }
  }, [loading, isFounder, user, navigate]);

  // Revoke invite
  const revokeInvite = async (inviteId) => {
    if (!window.confirm('Are you sure you want to revoke this invite?')) return;
    
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(
        `${API}/api/invites/${inviteId}?org_id=${orgId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchInvites();
    } catch (err) {
      console.error('Failed to revoke invite:', err);
    }
  };

  // Resend invite
  const resendInvite = async (inviteId) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API}/api/invites/${inviteId}/resend?org_id=${orgId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Copy new link to clipboard
      navigator.clipboard.writeText(response.data.invite_link);
      alert('New invite link copied to clipboard!');
      fetchInvites();
    } catch (err) {
      console.error('Failed to resend invite:', err);
      alert('Failed to resend invite');
    }
  };

  // Toggle finance access permission
  const toggleFinanceAccess = async (userId, currentValue) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.patch(
        `${API}/api/invites/team-members/${userId}/permissions?org_id=${orgId}`,
        { manager_finance_access: !currentValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchTeamMembers();
    } catch (err) {
      console.error('Failed to toggle permission:', err);
      alert('Failed to update permission');
    }
  };

  // Toggle staff invite permission
  const toggleInviteStaff = async (userId, currentValue) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.patch(
        `${API}/api/invites/team-members/${userId}/permissions?org_id=${orgId}`,
        { can_invite_staff: !currentValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchTeamMembers();
    } catch (err) {
      console.error('Failed to toggle permission:', err);
      alert('Failed to update permission');
    }
  };

  // Show loading
  if (loading) {
    return (
      <div className="page" data-testid="team-members-page-loading">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
          <div className="loading-spinner" />
        </div>
      </div>
    );
  }

  // Don't render for users without access (Staff or unknown)
  if (!isFounder() && user?.role !== 'MANAGER') {
    return null;
  }

  // Managers without invite permission can view but not invite
  const canInvite = invitePermissions.can_invite;

  const getStatusBadge = (status) => {
    const styles = {
      pending: { background: '#f59e0b20', color: '#f59e0b', border: '1px solid #f59e0b40' },
      accepted: { background: '#22c55e20', color: '#22c55e', border: '1px solid #22c55e40' },
      expired: { background: '#64748b20', color: '#64748b', border: '1px solid #64748b40' },
      revoked: { background: '#ef444420', color: '#ef4444', border: '1px solid #ef444440' }
    };
    return (
      <span style={{
        ...styles[status],
        padding: '4px 10px',
        borderRadius: '20px',
        fontSize: '0.75rem',
        fontWeight: 500,
        textTransform: 'capitalize'
      }}>
        {status}
      </span>
    );
  };

  return (
    <div className="page" data-testid="team-members-page">
      {/* Page Header */}
      <div className="page-header">
        <div className="page-header-content">
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
            <h1>Team Members</h1>
            <span style={{
              fontSize: '0.75rem',
              color: 'var(--status-success)',
              background: 'rgba(34, 197, 94, 0.1)',
              padding: '4px 10px',
              borderRadius: 'var(--radius-full)',
              border: '1px solid rgba(34, 197, 94, 0.3)'
            }}>
              Phase 1 Active
            </span>
          </div>
          <p>Invite and manage team access</p>
        </div>
        <div className="page-header-actions">
          {canInvite && (
            <button
              className="btn btn-primary"
              onClick={() => setShowInviteModal(true)}
              data-testid="invite-member-btn"
              style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}
            >
              <UserPlus size={16} />
              <span>Invite Team Member</span>
            </button>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xl)', maxWidth: '900px' }}>
        {/* Pending Invites */}
        <GlassPanel title="Invites" titleIcon={Mail} data-testid="invites-card">
          {invitesLoading ? (
            <div style={{ padding: '40px', textAlign: 'center' }}>
              <div className="loading-spinner" style={{ margin: '0 auto' }} />
            </div>
          ) : invites.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>
              <UserPlus size={32} style={{ opacity: 0.3, marginBottom: '12px' }} />
              <p>No invites yet. Click &quot;Invite Manager&quot; to add team members.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
              {invites.map((invite) => (
                <div
                  key={invite.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: 'var(--space-md)',
                    background: 'var(--glass-bg)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--glass-border)'
                  }}
                  data-testid={`invite-row-${invite.id}`}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: 'var(--radius-full)',
                      background: 'var(--accent-primary-muted)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Briefcase size={18} style={{ color: 'var(--accent-primary)' }} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>
                        {invite.email}
                      </div>
                      <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
                        {invite.role} • Invited {new Date(invite.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
                    {getStatusBadge(invite.status)}
                    {invite.status === 'pending' && (
                      <>
                        <button
                          onClick={() => resendInvite(invite.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'var(--accent-primary)',
                            cursor: 'pointer',
                            padding: '8px',
                            borderRadius: 'var(--radius-sm)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                          title="Resend invite"
                          data-testid={`resend-${invite.id}`}
                        >
                          <RefreshCw size={14} />
                        </button>
                        <button
                          onClick={() => revokeInvite(invite.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'var(--status-danger)',
                            cursor: 'pointer',
                            padding: '8px',
                            borderRadius: 'var(--radius-sm)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                          title="Revoke invite"
                          data-testid={`revoke-${invite.id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </>
                    )}
                    {(invite.status === 'expired') && (
                      <button
                        onClick={() => resendInvite(invite.id)}
                        style={{
                          background: 'var(--accent-primary)',
                          border: 'none',
                          color: 'white',
                          cursor: 'pointer',
                          padding: '6px 12px',
                          borderRadius: 'var(--radius-sm)',
                          fontSize: '0.8125rem',
                          fontWeight: 500,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                        data-testid={`resend-expired-${invite.id}`}
                      >
                        <RefreshCw size={12} />
                        Resend
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </GlassPanel>

        {/* Active Team Members */}
        <GlassPanel title="Active Team Members" titleIcon={Users} data-testid="team-members-card">
          {membersLoading ? (
            <div style={{ padding: '40px', textAlign: 'center' }}>
              <div className="loading-spinner" style={{ margin: '0 auto' }} />
            </div>
          ) : teamMembers.filter(m => m.role === 'MANAGER' || m.role === 'STAFF').length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>
              <Users size={32} style={{ opacity: 0.3, marginBottom: '12px' }} />
              <p>No team members yet.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
              {/* Managers Section */}
              {teamMembers.filter(m => m.role === 'MANAGER').length > 0 && (
                <>
                  <div style={{ 
                    fontSize: '0.75rem', 
                    fontWeight: 600, 
                    color: 'var(--status-info)',
                    textTransform: 'uppercase',
                    marginBottom: '4px'
                  }}>
                    Managers
                  </div>
                  {teamMembers.filter(m => m.role === 'MANAGER').map((member) => (
                    <div
                      key={member.user_id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: 'var(--space-md)',
                        background: 'var(--glass-bg)',
                        borderRadius: 'var(--radius-md)',
                        border: '1px solid var(--glass-border)'
                      }}
                      data-testid={`member-row-${member.user_id}`}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: 'var(--radius-full)',
                          background: 'var(--status-info)20',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <Briefcase size={18} style={{ color: 'var(--status-info)' }} />
                        </div>
                        <div>
                          <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>
                            {member.name}
                          </div>
                          <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
                            {member.email}
                          </div>
                        </div>
                      </div>
                      
                      {/* Permission Toggles - only for Founder */}
                      {isFounder() && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-lg)' }}>
                          {/* Finance Access Toggle */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                              Finance
                            </span>
                            <button
                              onClick={() => toggleFinanceAccess(member.user_id, member.manager_finance_access)}
                              style={{
                                width: '40px',
                                height: '22px',
                                borderRadius: '11px',
                                border: 'none',
                                cursor: 'pointer',
                                background: member.manager_finance_access 
                                  ? 'var(--status-success)' 
                                  : 'var(--glass-border)',
                                position: 'relative',
                                transition: 'background 0.2s'
                              }}
                              data-testid={`finance-toggle-${member.user_id}`}
                              title={member.manager_finance_access ? 'Disable finance access' : 'Enable finance access'}
                            >
                              <span style={{
                                position: 'absolute',
                                top: '2px',
                                left: member.manager_finance_access ? '20px' : '2px',
                                width: '18px',
                                height: '18px',
                                borderRadius: '50%',
                                background: 'white',
                                transition: 'left 0.2s',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                              }} />
                            </button>
                          </div>
                          
                          {/* Can Invite Staff Toggle */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                              Invite Staff
                            </span>
                            <button
                              onClick={() => toggleInviteStaff(member.user_id, member.can_invite_staff)}
                              style={{
                                width: '40px',
                                height: '22px',
                                borderRadius: '11px',
                                border: 'none',
                                cursor: 'pointer',
                                background: member.can_invite_staff 
                                  ? 'var(--accent-primary)' 
                                  : 'var(--glass-border)',
                                position: 'relative',
                                transition: 'background 0.2s'
                              }}
                              data-testid={`invite-staff-toggle-${member.user_id}`}
                              title={member.can_invite_staff ? 'Disable staff invite' : 'Enable staff invite'}
                            >
                              <span style={{
                                position: 'absolute',
                                top: '2px',
                                left: member.can_invite_staff ? '20px' : '2px',
                                width: '18px',
                                height: '18px',
                                borderRadius: '50%',
                                background: 'white',
                                transition: 'left 0.2s',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                              }} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </>
              )}
              
              {/* Staff Section */}
              {teamMembers.filter(m => m.role === 'STAFF').length > 0 && (
                <>
                  <div style={{ 
                    fontSize: '0.75rem', 
                    fontWeight: 600, 
                    color: 'var(--status-success)',
                    textTransform: 'uppercase',
                    marginTop: 'var(--space-md)',
                    marginBottom: '4px'
                  }}>
                    Staff
                  </div>
                  {teamMembers.filter(m => m.role === 'STAFF').map((member) => (
                    <div
                      key={member.user_id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: 'var(--space-md)',
                        background: 'var(--glass-bg)',
                        borderRadius: 'var(--radius-md)',
                        border: '1px solid var(--glass-border)'
                      }}
                      data-testid={`member-row-${member.user_id}`}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: 'var(--radius-full)',
                          background: 'rgba(34, 197, 94, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <HardHat size={18} style={{ color: 'var(--status-success)' }} />
                        </div>
                        <div>
                          <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>
                            {member.name}
                          </div>
                          <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
                            {member.email}
                          </div>
                        </div>
                      </div>
                      
                      {/* Staff has no toggleable permissions */}
                      <span style={{
                        fontSize: '0.6875rem',
                        fontWeight: 500,
                        color: 'var(--text-muted)',
                        background: 'var(--glass-bg)',
                        padding: '4px 10px',
                        borderRadius: 'var(--radius-full)',
                        border: '1px solid var(--glass-border)'
                      }}>
                        Restricted Access
                      </span>
                    </div>
                  ))}
                </>
              )}
              
              {/* Info note about permissions */}
              {isFounder() && (
                <div style={{
                  marginTop: 'var(--space-sm)',
                  padding: 'var(--space-md)',
                  background: 'rgba(59, 130, 246, 0.05)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px dashed rgba(59, 130, 246, 0.3)',
                  fontSize: '0.8125rem',
                  color: 'var(--text-muted)'
                }}>
                  <strong style={{ color: 'var(--accent-primary)' }}>Manager Permissions:</strong>
                  <br />• <strong>Finance</strong> - Access to Finance-Life, invoices, Xero, bank analysis
                  <br />• <strong>Invite Staff</strong> - Can create Staff invites (not Manager invites)
                  <br /><br />
                  <strong style={{ color: 'var(--status-success)' }}>Staff</strong> have restricted access: assigned tasks, view materials. No finance, no invites, no delete.
                </div>
              )}
            </div>
          )}
        </GlassPanel>

        {/* Access Model Card */}
        <GlassPanel title="Access Model" titleIcon={Users} data-testid="access-model-card">
          <div style={{ display: 'grid', gap: 'var(--space-lg)', padding: 'var(--space-md) 0' }}>
            {roleDefinitions.map((role, index) => (
              <div 
                key={role.role}
                style={{
                  display: 'flex',
                  gap: 'var(--space-lg)',
                  padding: 'var(--space-md)',
                  background: 'var(--glass-bg)',
                  borderRadius: 'var(--radius-lg)',
                  border: role.invitable 
                    ? '1px solid var(--accent-primary)40'
                    : '1px solid var(--glass-border)',
                  position: 'relative'
                }}
                data-testid={`role-card-${index}`}
              >
                {role.invitable && (
                  <span style={{
                    position: 'absolute',
                    top: '-10px',
                    right: '16px',
                    background: 'var(--accent-primary)',
                    color: 'white',
                    fontSize: '0.6875rem',
                    fontWeight: 600,
                    padding: '2px 8px',
                    borderRadius: 'var(--radius-full)'
                  }}>
                    NOW AVAILABLE
                  </span>
                )}
                <div style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: 'var(--radius-md)',
                  background: `${role.color}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <role.icon size={22} style={{ color: role.color }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ 
                    fontWeight: '600', 
                    color: 'var(--text-primary)',
                    marginBottom: 'var(--space-xs)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-sm)'
                  }}>
                    {role.role}
                    {role.subLabel && (
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: '400' }}>
                        {role.subLabel}
                      </span>
                    )}
                  </div>
                  <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {role.permissions.map((perm, i) => (
                      <li key={i} style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                        <span style={{ width: '4px', height: '4px', borderRadius: '50%', background: 'var(--text-muted)', flexShrink: 0 }} />
                        {perm}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </GlassPanel>

        {/* Why Access is Restricted Card */}
        <GlassPanel title="Security & Auditability" titleIcon={Info} data-testid="security-card">
          <div style={{ padding: 'var(--space-md) 0', color: 'var(--text-secondary)', lineHeight: '1.7', fontSize: '0.9375rem' }}>
            <p style={{ margin: '0 0 var(--space-md) 0' }}>
              TradeLife enforces strict permission boundaries to protect financial data and maintain
              accountability. All invite actions are logged in the audit trail.
            </p>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li>Managers cannot access Finance-Life, margins, or price intelligence</li>
              <li>All access attempts are logged</li>
              <li>Only Founders can invite new users</li>
              <li>Invites expire after 7 days</li>
            </ul>
          </div>
        </GlassPanel>
      </div>

      {/* Invite Modal */}
      <InviteModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onSuccess={fetchInvites}
        orgId={orgId}
        canInviteManager={invitePermissions.can_invite_manager}
        canInviteStaff={invitePermissions.can_invite_staff}
      />
    </div>
  );
}

// Modal Styles
const modalStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '24px'
  },
  modal: {
    width: '100%',
    maxWidth: '480px',
    background: '#1e222d',
    borderRadius: '20px',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 24px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    margin: 0,
    fontSize: '1.125rem',
    fontWeight: 600,
    color: '#f1f5f9'
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    color: '#64748b',
    cursor: 'pointer',
    padding: '4px'
  },
  form: {
    padding: '24px'
  },
  fieldGroup: {
    marginBottom: '20px'
  },
  label: {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: '#94a3b8',
    marginBottom: '8px'
  },
  inputWrapper: {
    position: 'relative'
  },
  inputIcon: {
    position: 'absolute',
    left: '14px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#64748b'
  },
  input: {
    width: '100%',
    padding: '12px 14px 12px 44px',
    fontSize: '0.9375rem',
    background: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '10px',
    color: '#f1f5f9',
    outline: 'none',
    boxSizing: 'border-box'
  },
  roleSelector: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  roleOption: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px',
    background: 'rgba(255, 255, 255, 0.03)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '10px',
    cursor: 'pointer',
    color: '#94a3b8',
    transition: 'all 0.2s'
  },
  roleOptionActive: {
    background: 'rgba(59, 130, 246, 0.1)',
    borderColor: '#3b82f6',
    color: '#3b82f6'
  },
  roleTitle: {
    fontWeight: 600,
    fontSize: '0.9375rem'
  },
  roleDesc: {
    fontSize: '0.8125rem',
    opacity: 0.7
  },
  roleNote: {
    fontSize: '0.75rem',
    color: '#64748b',
    marginTop: '8px',
    fontStyle: 'italic'
  },
  errorBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px',
    background: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    borderRadius: '10px',
    fontSize: '0.875rem',
    color: '#ef4444',
    marginBottom: '20px'
  },
  actions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end'
  },
  cancelBtn: {
    padding: '10px 20px',
    fontSize: '0.9375rem',
    fontWeight: 500,
    color: '#94a3b8',
    background: 'transparent',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '10px',
    cursor: 'pointer'
  },
  submitBtn: {
    padding: '10px 20px',
    fontSize: '0.9375rem',
    fontWeight: 600,
    color: 'white',
    background: '#3b82f6',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer'
  },
  successContent: {
    padding: '32px 24px',
    textAlign: 'center'
  },
  successTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: '#f1f5f9',
    margin: '0 0 8px'
  },
  successText: {
    fontSize: '0.9375rem',
    color: '#94a3b8',
    margin: '0 0 24px'
  },
  linkBox: {
    display: 'flex',
    gap: '8px',
    marginBottom: '12px'
  },
  linkInput: {
    flex: 1,
    padding: '12px',
    fontSize: '0.8125rem',
    background: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '8px',
    color: '#94a3b8',
    outline: 'none'
  },
  copyBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '12px 16px',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: 'white',
    background: '#3b82f6',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    whiteSpace: 'nowrap'
  },
  expiryNote: {
    fontSize: '0.75rem',
    color: '#64748b',
    margin: '0 0 24px'
  },
  doneBtn: {
    width: '100%',
    padding: '12px',
    fontSize: '0.9375rem',
    fontWeight: 600,
    color: 'white',
    background: '#22c55e',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer'
  }
};

export default TeamMembers;
